/**
 * F-132: Mini App page for /me/qa.
 * Question (≤160) + answer (≤400) editor list.
 */
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  Modal,
  Spinner,
  Textarea,
  toast,
} from '@/components/atoms';
import { specialists as api } from '@/api';
import type { QnARow } from '@/api/specialists';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

interface EditorState {
  id?: number;
  question: string;
  answer: string;
  errors: { question?: string; answer?: string };
}

export function MyQAPage(): JSX.Element {
  const [rows, setRows] = useState<QnARow[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busy, setBusy] = useState(false);
  // F-180 C7: replace window.confirm with ConfirmDialog.
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setError(null);
    api
      .fetchMyQA()
      .then((r) => setRows(r.results))
      .catch((err) => {
        logger.error('qa load failed', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setRows(null);
      });
  }, [reloadKey]);

  const openNew = (): void =>
    setEditor({ question: '', answer: '', errors: {} });
  const openEdit = (row: QnARow): void =>
    setEditor({ id: row.id, question: row.question, answer: row.answer, errors: {} });

  const save = async (): Promise<void> => {
    if (!editor) return;
    const q = editor.question.trim().slice(0, 160);
    const a = editor.answer.trim().slice(0, 400);
    // F-180 C6: field-level errors instead of a toast.
    const errors: EditorState['errors'] = {};
    if (!q) errors.question = 'Введіть запитання';
    if (!a) errors.answer = 'Введіть відповідь';
    if (errors.question || errors.answer) {
      setEditor({ ...editor, errors });
      return;
    }
    setBusy(true);
    try {
      let row: QnARow;
      if (editor.id) {
        row = await api.updateQA(editor.id, { question: q, answer: a });
        setRows((prev) => (prev ?? []).map((r) => (r.id === row.id ? row : r)));
      } else {
        row = await api.createQA(q, a);
        setRows((prev) => [...(prev ?? []), row]);
      }
      setEditor(null);
      toast.success('Збережено');
    } catch (err) {
      logger.error('qa save failed', err);
      toast.error('Не вдалось зберегти');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleteId == null) return;
    setDeleting(true);
    try {
      await api.deleteQA(deleteId);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      logger.error('qa delete failed', err);
      toast.error('Не вдалось видалити');
    } finally {
      setDeleting(false);
    }
  };

  if (error) {
    return (
      <Layout title="Q&A">
        <ErrorState onRetry={() => setReloadKey((k) => k + 1)} />
      </Layout>
    );
  }
  if (rows === null) {
    return (
      <Layout title="Q&A">
        <Spinner />
      </Layout>
    );
  }

  return (
    <Layout title="Q&A">
      <Button onClick={openNew}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="plus" size={18} />
          <span>Додати Q&A</span>
        </span>
      </Button>

      {rows.length === 0 ? (
        <EmptyState
          icon="info"
          message="Додай 2-3 типові питання — клієнтам легше довіритись."
        />
      ) : (
        <div className={styles.list}>
          {rows.map((row) => (
            <Card key={row.id} interactive onClick={() => openEdit(row)}>
              <strong>{row.question}</strong>
              <div className={styles.note} style={{ marginTop: 6 }}>
                {row.answer}
              </div>
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(row.id);
                }}
                style={{ marginTop: 8, padding: '4px 8px' }}
              >
                <Icon name="x" size={16} />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={Boolean(editor)}
        title={editor?.id ? 'Редагувати Q&A' : 'Нове Q&A'}
        onClose={() => setEditor(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setEditor(null)}>
              Скасувати
            </Button>
            <Button disabled={busy} onClick={() => void save()}>
              Зберегти
            </Button>
          </>
        }
      >
        <Input
          placeholder="Запитання (≤160)"
          value={editor?.question ?? ''}
          error={editor?.errors.question}
          onChange={(e) =>
            setEditor((prev) =>
              prev
                ? {
                    ...prev,
                    question: e.target.value.slice(0, 160),
                    errors: { ...prev.errors, question: undefined },
                  }
                : prev,
            )
          }
        />
        <Textarea
          placeholder="Відповідь (≤400)"
          value={editor?.answer ?? ''}
          error={editor?.errors.answer}
          onChange={(e) =>
            setEditor((prev) =>
              prev
                ? {
                    ...prev,
                    answer: e.target.value.slice(0, 400),
                    errors: { ...prev.errors, answer: undefined },
                  }
                : prev,
            )
          }
          rows={4}
          style={{ marginTop: 12 }}
        />
      </Modal>

      <ConfirmDialog
        open={deleteId != null}
        title="Видалити Q&A?"
        body="Відвідувачі більше не побачать цей запис."
        confirmLabel="Видалити"
        tone="destructive"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Layout>
  );
}
